/**
 * GroupRoundSetup - Enhanced Player & Scorecard Selection for Group Rounds
 * 
 * Flow: Pick Players → Holes/Nine → Scorecard Type → Start Round
 * (Course is already selected from AddScorePage before this modal opens)
 * 
 * Scorecard Types:
 * - QuickCard (Voice-powered, default)
 * - Manual (Traditional hole-by-hole entry)
 * - Voice Only (Speak scores, minimal UI)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { parseOptionalHandicap, handicapLabel } from '../utils/handicap';
import { getRoundParData } from '../utils/scorePayload';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Search,
  X,
  Check,
  ChevronRight,
  ChevronLeft,
  User,
  DollarSign,
  Plus,
  AlertCircle,
  Mic,
  Grid3X3,
  PenLine,
  Zap
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import UserBubble from './UserBubble';
import EntryFeeSelector from './EntryFeeSelector';
import soundEffects from '../utils/soundEffects';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const MAX_PLAYERS = 4;
const MIN_PLAYERS = 2;

// ============ SCORECARD CAROUSEL COMPONENT ============
const ScorecardCarousel = ({ scorecardTypes, selectedScorecardType, onSelect }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(0);
  const audioContextRef = useRef(null);

  // Play swoosh sound
  const playSwoosh = useCallback(() => {
    try {
      const audioContext = audioContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
      
      // Resume audio context if suspended (required for mobile browsers)
      if (audioContext.state === 'suspended') {
        audioContext.resume();
      }
      
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(250, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(60, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.06, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (e) {
      // Sound is an enhancement, silently fail if not supported
    }
  }, []);

  const goToCard = useCallback((index) => {
    if (index >= 0 && index < scorecardTypes.length && index !== currentIndex) {
      playSwoosh();
      setCurrentIndex(index);
    }
  }, [currentIndex, scorecardTypes.length, playSwoosh]);

  // Touch handlers
  const handleTouchStart = (e) => {
    setIsDragging(true);
    setDragStart(e.touches[0].clientX);
    setDragOffset(0);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.touches[0].clientX - dragStart);
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    const threshold = 50;
    if (dragOffset < -threshold && currentIndex < scorecardTypes.length - 1) {
      goToCard(currentIndex + 1);
    } else if (dragOffset > threshold && currentIndex > 0) {
      goToCard(currentIndex - 1);
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  // Mouse handlers
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart(e.clientX);
    setDragOffset(0);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setDragOffset(e.clientX - dragStart);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const threshold = 50;
    if (dragOffset < -threshold && currentIndex < scorecardTypes.length - 1) {
      goToCard(currentIndex + 1);
    } else if (dragOffset > threshold && currentIndex > 0) {
      goToCard(currentIndex - 1);
    }
    setIsDragging(false);
    setDragOffset(0);
  };

  const getGlowColor = (color) => {
    switch(color) {
      case 'green': return 'rgba(34, 197, 94, 0.5)';
      case 'blue': return 'rgba(59, 130, 246, 0.5)';
      case 'amber': return 'rgba(245, 158, 11, 0.5)';
      default: return 'rgba(100, 100, 100, 0.3)';
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-[var(--ps-text-secondary)] text-sm text-center">
        Swipe to choose your scorecard style
      </p>
      
      {/* Card counter */}
      <div className="flex justify-center">
        <span className="text-[var(--ps-text-muted)] text-sm font-medium">
          {currentIndex + 1} / {scorecardTypes.length}
        </span>
      </div>

      {/* Carousel container */}
      <div
        className="relative h-72 select-none"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {scorecardTypes.map((type, index) => {
          const Icon = type.icon;
          const offset = index - currentIndex;
          const isSelected = selectedScorecardType?.id === type.id;
          
          if (Math.abs(offset) > 1) return null;
          
          const translateX = isDragging 
            ? (offset * 100) + (dragOffset / 3)
            : offset * 100;
          const scale = offset === 0 ? 1 : 0.85;
          const opacity = offset === 0 ? 1 : 0.4;
          const zIndex = 10 - Math.abs(offset);
          
          return (
            <div
              key={type.id}
              className={`
                absolute inset-x-4 top-0 h-full
                transition-all duration-300 ease-out
                ${isDragging ? 'transition-none' : ''}
              `}
              style={{
                transform: `translateX(${translateX}%) scale(${scale})`,
                opacity: Math.max(0, opacity),
                zIndex,
                pointerEvents: offset === 0 ? 'auto' : 'none'
              }}
            >
              <button
                onClick={() => {
                  if (!isDragging && offset === 0) {
                    onSelect(type);
                  }
                }}
                className={`
                  w-full h-full rounded-2xl p-5
                  border-2 transition-all text-left
                  ${isSelected 
                    ? 'border-green-500 bg-green-500/10' 
                    : `border-[var(--ps-border)] bg-[var(--ps-bg-elevated)]`
                  }
                `}
                style={{
                  boxShadow: offset === 0 
                    ? `0 0 40px ${getGlowColor(type.color)}, 0 0 80px ${getGlowColor(type.color).replace('0.5', '0.2')}`
                    : 'none'
                }}
                data-testid={`scorecard-${type.id}`}
              >
                <div className="flex flex-col items-center text-center h-full justify-between py-2">
                  {/* Icon */}
                  <div className={`w-20 h-20 rounded-2xl flex items-center justify-center mb-3 ${
                    type.color === 'green' ? 'bg-green-500/20' :
                    type.color === 'blue' ? 'bg-blue-500/20' :
                    'bg-amber-500/20'
                  }`}>
                    <Icon className={`w-10 h-10 ${
                      type.color === 'green' ? 'text-green-400' :
                      type.color === 'blue' ? 'text-blue-400' :
                      'text-amber-400'
                    }`} />
                  </div>
                  
                  {/* Title & Description */}
                  <div className="flex-1">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <h3 className="font-bold text-xl text-[var(--ps-text-primary)]">{type.name}</h3>
                      {isSelected && <Check className="w-5 h-5 text-green-400" />}
                    </div>
                    <p className="text-sm text-[var(--ps-text-muted)] mb-3">{type.description}</p>
                    
                    {/* Features */}
                    <div className="flex flex-wrap justify-center gap-1.5">
                      {type.features.map((feature, idx) => (
                        <span 
                          key={idx}
                          className="text-xs px-2 py-1 bg-[var(--ps-bg-hover)] text-[var(--ps-text-secondary)] rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Tap indicator */}
                  <div className={`mt-3 text-sm font-medium ${
                    type.color === 'green' ? 'text-green-400' :
                    type.color === 'blue' ? 'text-blue-400' :
                    'text-amber-400'
                  }`}>
                    Tap to select
                  </div>
                </div>
              </button>
            </div>
          );
        })}
      </div>

      {/* Swipe hint */}
      <div className="flex items-center justify-center gap-3 text-[var(--ps-text-muted)] text-sm">
        <ChevronLeft className={`w-4 h-4 ${currentIndex === 0 ? 'opacity-20' : 'opacity-70'}`} />
        <span>Swipe to browse</span>
        <ChevronRight className={`w-4 h-4 ${currentIndex === scorecardTypes.length - 1 ? 'opacity-20' : 'opacity-70'}`} />
      </div>

      {selectedScorecardType && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-xl">
          <div className="flex items-center justify-center gap-2 text-green-400">
            <Check className="w-5 h-5" />
            <span className="font-medium">Selected: {selectedScorecardType.name}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Scorecard type definitions
const SCORECARD_TYPES = [
  {
    id: 'quickcard',
    name: 'QuickCard',
    description: 'Voice + tap scoring with live scorecard',
    icon: Zap,
    color: 'green',
    features: ['Voice entry', 'Tap to score', 'Live sync', 'Best for groups']
  },
  {
    id: 'voice',
    name: 'Voice Only',
    description: 'Speak all scores hands-free',
    icon: Mic,
    color: 'blue',
    features: ['Hands-free', 'Speak naturally', '"Player got a 4"', 'Minimal UI']
  },
  {
    id: 'manual',
    name: 'Manual Entry',
    description: 'Traditional scorecard input',
    icon: PenLine,
    color: 'amber',
    features: ['Tap each hole', 'Classic layout', 'Full control', 'Detailed stats']
  }
];

/**
 * Group Round Setup Modal - Multi-step flow
 */
const GroupRoundSetup = ({
  isOpen,
  onClose,
  selectedCourse,
  numberOfHoles: initialHoles = 18,
  startingNine: initialNine = 'front',
  currentUser,
  onStartRound
}) => {
  const navigate = useNavigate();
  
  // Multi-step state
  const [currentStep, setCurrentStep] = useState(1); // 1: Setup, 2: Players, 3: Scorecard
  
  // Holes & Nine selection (Step 1)
  const [numberOfHoles, setNumberOfHoles] = useState(initialHoles);
  const [startingNine, setStartingNine] = useState(initialNine);
  
  // Selected players (host is always first)
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  // Buddies (quick select)
  const [buddies, setBuddies] = useState([]);
  const [loadingBuddies, setLoadingBuddies] = useState(true);
  
  // Guest modal
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [guestHandicap, setGuestHandicap] = useState('');
  
  // Entry fee
  const [entryFee, setEntryFee] = useState(0);
  const [entryFeeType, setEntryFeeType] = useState('coins'); // 'coins' or 'wallet'
  const [showFeeSetup, setShowFeeSetup] = useState(false);
  
  // Scorecard selection (Step 3)
  const [selectedScorecardType, setSelectedScorecardType] = useState(null);
  const [showScorecardPreview, setShowScorecardPreview] = useState(false);
  const [previewCardType, setPreviewCardType] = useState(null);
  
  // Creating round
  const [isCreating, setIsCreating] = useState(false);

  // Ref for scrollable content area
  const contentRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Check if course is selected - show error if not
      if (!selectedCourse) {
        toast.error('Please select a golf course first');
        onClose();
        return;
      }
      
      setCurrentStep(1);
      setNumberOfHoles(initialHoles);
      setStartingNine(initialNine);
      setSelectedScorecardType(null);
      setShowScorecardPreview(false);
      setPreviewCardType(null);
    }
  }, [isOpen, initialHoles, initialNine, selectedCourse, onClose]);

  // Scroll to top when step changes
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  // Initialize with current user as host
  useEffect(() => {
    if (isOpen && currentUser && selectedPlayers.length === 0) {
      setSelectedPlayers([{
        player_id: currentUser.user_id,
        name: currentUser.name || 'You',
        picture: currentUser.picture,
        handicap: currentUser.handicap ?? null,
        bubble_color: currentUser.bubble_color || 'green',
        is_host: true,
        is_guest: false
      }]);
    }
  }, [isOpen, currentUser]);

  // Fetch buddies on mount
  useEffect(() => {
    if (isOpen) {
      fetchBuddies();
    }
  }, [isOpen]);

  const fetchBuddies = async () => {
    try {
      setLoadingBuddies(true);
      const response = await axios.get(`${API}/members?limit=50`);
      const allMembers = response.data.members || [];
      const myBuddies = allMembers.filter(m => m.is_following && !m.is_self);
      setBuddies(myBuddies);
    } catch (error) {
      console.error('Error fetching buddies:', error);
    } finally {
      setLoadingBuddies(false);
    }
  };

  // Search members
  const handleSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    try {
      setIsSearching(true);
      const response = await axios.get(`${API}/members?search=${encodeURIComponent(query)}&limit=10`);
      const results = (response.data.members || []).filter(m => !m.is_self);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  // Add a player
  const addPlayer = (member) => {
    if (selectedPlayers.length >= MAX_PLAYERS) {
      toast.error(`Maximum ${MAX_PLAYERS} players allowed`);
      return;
    }
    
    if (selectedPlayers.find(p => p.player_id === member.user_id)) {
      toast.info(`${member.name} is already in your group`);
      return;
    }
    
    soundEffects.playSwish();
    setSelectedPlayers(prev => [...prev, {
      player_id: member.user_id,
      name: member.name,
      picture: member.picture,
      handicap: member.handicap,
      bubble_color: member.bubble_color || 'green',
      is_host: false,
      is_guest: false
    }]);
    
    setSearchQuery('');
    setSearchResults([]);
    toast.success(`${member.name} added to group`);
  };

  // Add a guest player
  const addGuest = () => {
    if (!guestName.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    if (selectedPlayers.length >= MAX_PLAYERS) {
      toast.error(`Maximum ${MAX_PLAYERS} players allowed`);
      return;
    }
    
    soundEffects.playSwish();
    const guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    setSelectedPlayers(prev => [...prev, {
      player_id: guestId,
      name: guestName.trim(),
      picture: null,
      handicap: parseOptionalHandicap(guestHandicap),
      handicap_source: guestHandicap.trim() !== '' ? 'manual' : null,
      bubble_color: 'gray',
      is_host: false,
      is_guest: true
    }]);
    
    setGuestName('');
    setGuestHandicap('');
    setShowAddGuest(false);
    toast.success(`${guestName.trim()} added as guest`);
  };

  // Remove a player
  const removePlayer = (playerId) => {
    const player = selectedPlayers.find(p => p.player_id === playerId);
    if (player?.is_host) {
      toast.error("Can't remove yourself from the group");
      return;
    }
    
    soundEffects.playSwish();
    setSelectedPlayers(prev => prev.filter(p => p.player_id !== playerId));
  };

  // Handle scorecard type selection
  const handleScorecardTypeSelect = (type) => {
    setPreviewCardType(type);
    setShowScorecardPreview(true);
  };

  // Confirm scorecard selection
  const confirmScorecardSelection = () => {
    setSelectedScorecardType(previewCardType);
    setShowScorecardPreview(false);
    soundEffects.playSwish();
    // Automatically start the round after confirming scorecard selection
    // This provides better UX - user chose their card, now let's go!
    setTimeout(() => {
      startRoundWithCard(previewCardType);
    }, 100);
  };

  // Start the round with the selected card (can be called with card directly)
  const startRoundWithCard = async (cardType) => {
    const scorecardToUse = cardType || selectedScorecardType;
    
    if (selectedPlayers.length < MIN_PLAYERS) {
      toast.error(`Need at least ${MIN_PLAYERS} players to start a group round`);
      return;
    }
    
    if (!selectedCourse) {
      toast.error('Please select a course first');
      return;
    }

    if (!scorecardToUse) {
      toast.error('Please select a scorecard type');
      return;
    }
    
    setIsCreating(true);
    
    try {
      const response = await axios.post(`${API}/group-rounds/create`, {
        course_id: selectedCourse.id || selectedCourse.course_id,
        course_name: selectedCourse.name,
        course_par: getRoundParData(selectedCourse, numberOfHoles, startingNine)?.par,
        holes: numberOfHoles,
        starting_nine: startingNine,
        scorecard_type: scorecardToUse.id,
        players: selectedPlayers.map(p => ({
          player_id: p.player_id,
          name: p.name,
          handicap: p.handicap,
          is_host: p.is_host,
          is_guest: p.is_guest
        })),
        entry_fee: entryFee,
        entry_fee_type: entryFeeType,
        hole_pars: selectedCourse.hole_pars || [],
        hole_handicaps: selectedCourse.hole_handicaps || []
      });
      
      soundEffects.playSuccess();
      toast.success('Group round created! Let\'s play!');
      
      if (onStartRound) {
        onStartRound({
          roundId: response.data.round_id,
          players: selectedPlayers,
          course: selectedCourse,
          holes: numberOfHoles,
          startingNine,
          entryFee,
          entryFeeType,
          scorecardType: scorecardToUse.id,
          holePars: selectedCourse.hole_pars || [],
          holeHandicaps: selectedCourse.hole_handicaps || Array(18).fill(9)
        });
      }
      
      onClose();
      
    } catch (error) {
      console.error('Error creating group round:', error);
      toast.error(error.response?.data?.detail || 'Failed to create group round');
    } finally {
      setIsCreating(false);
    }
  };

  // Start the round (legacy function for footer button)
  const startRound = async () => {
    await startRoundWithCard(selectedScorecardType);
  };

  const isSelected = (userId) => {
    return selectedPlayers.some(p => p.player_id === userId);
  };

  // Navigation handlers
  const goToNextStep = () => {
    if (currentStep === 1) {
      // Step 1 is Players - need minimum players to continue
      if (selectedPlayers.length < MIN_PLAYERS) {
        toast.error(`Add at least ${MIN_PLAYERS} players to continue`);
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Step 2 is Holes/Nine - always can proceed
      setCurrentStep(3);
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onClose();
    }
  };

  // Get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return 'Add Players';
      case 2: return 'Round Setup';
      case 3: return 'Choose Scorecard';
      default: return 'Group Round';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-md w-[95vw] p-0 bg-[var(--ps-bg-surface)] border-[var(--ps-border)] overflow-hidden"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'min(85vh, 700px)',
          maxHeight: 'calc(100dvh - 40px)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--ps-border)]" style={{ flexShrink: 0 }}>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={goToPrevStep}
              className="text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)] p-1"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-lg font-semibold text-[var(--ps-text-primary)] flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                {getStepTitle()}
              </h2>
              <p className="text-xs text-[var(--ps-text-muted)]">
                {selectedCourse?.name || 'Group Round'} • Step {currentStep}/3
              </p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div 
          ref={contentRef}
          className="p-4 pb-6 overflow-y-auto"
          style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}
        >
          
          {/* STEP 1: Players Selection (FIRST) */}
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Selected Players */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[var(--ps-text-secondary)] text-sm">Your Group</Label>
                  <span className="text-sm text-[var(--ps-text-muted)]">{selectedPlayers.length}/{MAX_PLAYERS}</span>
                </div>
                <div className="space-y-2">
                  {selectedPlayers.map((player) => (
                    <div 
                      key={player.player_id}
                      className="flex items-center justify-between p-3 bg-[var(--ps-bg-elevated)] rounded-xl border border-[var(--ps-border)]"
                    >
                      <div className="flex items-center gap-3">
                        <UserBubble 
                          name={player.name} 
                          picture={player.picture}
                          color={player.bubble_color}
                          size="sm"
                          showName={false}
                        />
                        <div>
                          <p className="font-medium text-[var(--ps-text-primary)]">
                            {player.name}
                            {player.is_host && <span className="text-xs text-green-500 ml-2">(Host)</span>}
                            {player.is_guest && <span className="text-xs text-amber-500 ml-2">(Guest)</span>}
                          </p>
                          {(
                            <p data-testid={`group-setup-handicap-${player.player_id}`} className="text-xs text-[var(--ps-text-muted)]">HCP: {handicapLabel(player.handicap)}</p>
                          )}
                        </div>
                      </div>
                      {!player.is_host && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removePlayer(player.player_id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  {selectedPlayers.length < MAX_PLAYERS && (
                    <Button
                      variant="outline"
                      className="w-full border-dashed border-[var(--ps-border)] text-[var(--ps-text-muted)] hover:text-[var(--ps-text-primary)]"
                      onClick={() => setShowAddGuest(true)}
                      data-testid="add-guest-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Guest Player
                    </Button>
                  )}
                </div>
              </div>

              {/* Buddies / Quick Add */}
              {buddies.length > 0 && selectedPlayers.length < MAX_PLAYERS && (
                <div>
                  <Label className="text-[var(--ps-text-secondary)] text-sm mb-2 block">Quick Add from Buddies</Label>
                  <div className="flex flex-wrap gap-2">
                    {buddies
                      .filter(b => !isSelected(b.user_id))
                      .slice(0, 6)
                      .map((buddy) => (
                        <button
                          key={buddy.user_id}
                          onClick={() => addPlayer(buddy)}
                          className="flex items-center gap-2 px-3 py-2 bg-[var(--ps-bg-elevated)] rounded-full border border-[var(--ps-border)] hover:border-green-500 transition-colors"
                        >
                          <UserBubble 
                            name={buddy.name} 
                            picture={buddy.picture}
                            color={buddy.bubble_color}
                            size="xs"
                            showName={false}
                          />
                          <span className="text-sm text-[var(--ps-text-primary)]">{buddy.name.split(' ')[0]}</span>
                          <Plus className="w-3 h-3 text-green-500" />
                        </button>
                      ))}
                  </div>
                </div>
              )}

              {/* Search Users */}
              {selectedPlayers.length < MAX_PLAYERS && (
                <div>
                  <Label className="text-[var(--ps-text-secondary)] text-sm mb-2 block">Search Par Seeker Users</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--ps-text-muted)]" />
                    <Input
                      placeholder="Search by name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)]"
                      data-testid="search-users-input"
                    />
                  </div>
                  
                  {/* Search Results */}
                  {searchQuery && (
                    <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                      {isSearching ? (
                        <div className="p-4 text-center">
                          <div className="w-5 h-5 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto" />
                        </div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map((user) => (
                          <button
                            key={user.user_id}
                            onClick={() => addPlayer(user)}
                            disabled={isSelected(user.user_id) || selectedPlayers.length >= MAX_PLAYERS}
                            className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors ${
                              isSelected(user.user_id)
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-[var(--ps-bg-elevated)] hover:bg-[var(--ps-bg-hover)] border border-transparent'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <UserBubble 
                                name={user.name} 
                                picture={user.picture}
                                color={user.bubble_color}
                                size="sm"
                                showName={false}
                              />
                              <div className="text-left">
                                <p className="font-medium text-[var(--ps-text-primary)]">{user.name}</p>
                                {(
                                  <p data-testid={`group-buddy-handicap-${user.user_id}`} className="text-xs text-[var(--ps-text-muted)]">HCP: {handicapLabel(user.handicap)}</p>
                                )}
                              </div>
                            </div>
                            {isSelected(user.user_id) ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <Plus className="w-4 h-4 text-[var(--ps-text-muted)]" />
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="text-sm text-[var(--ps-text-muted)] text-center py-4">
                          No users found
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Entry Fee - Using unified EntryFeeSelector */}
              <div>
                <EntryFeeSelector
                  entryFee={entryFee}
                  entryFeeType={entryFeeType}
                  onChangeValue={setEntryFee}
                  onChangeType={setEntryFeeType}
                  playerCount={selectedPlayers.length}
                  showTotalPot={true}
                  compact={true}
                />
              </div>
            </div>
          )}

          {/* STEP 2: Round Setup (Holes & Nine) */}
          {currentStep === 2 && (
            <div className="space-y-6">
              {/* Holes Selection */}
              <div>
                <Label className="text-[var(--ps-text-secondary)] text-sm mb-3 block">How many holes?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setNumberOfHoles(9)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      numberOfHoles === 9 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-[var(--ps-text-muted)]'
                    }`}
                    data-testid="holes-9-btn"
                  >
                    <div className="text-3xl font-bold text-[var(--ps-text-primary)]">9</div>
                    <div className="text-sm text-[var(--ps-text-muted)]">Holes</div>
                  </button>
                  <button
                    onClick={() => setNumberOfHoles(18)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      numberOfHoles === 18 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-[var(--ps-text-muted)]'
                    }`}
                    data-testid="holes-18-btn"
                  >
                    <div className="text-3xl font-bold text-[var(--ps-text-primary)]">18</div>
                    <div className="text-sm text-[var(--ps-text-muted)]">Holes</div>
                  </button>
                </div>
              </div>

              {/* Starting Nine Selection */}
              <div>
                <Label className="text-[var(--ps-text-secondary)] text-sm mb-3 block">Starting Nine</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setStartingNine('front')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      startingNine === 'front' 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-[var(--ps-text-muted)]'
                    }`}
                    data-testid="front-9-btn"
                  >
                    <div className="text-lg font-bold text-[var(--ps-text-primary)]">Front 9 First</div>
                    <div className="text-sm text-[var(--ps-text-muted)]">Holes 1-9</div>
                  </button>
                  <button
                    onClick={() => setStartingNine('back')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      startingNine === 'back' 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-[var(--ps-border)] bg-[var(--ps-bg-elevated)] hover:border-[var(--ps-text-muted)]'
                    }`}
                    data-testid="back-9-btn"
                  >
                    <div className="text-lg font-bold text-[var(--ps-text-primary)]">Back 9 First</div>
                    <div className="text-sm text-[var(--ps-text-muted)]">Holes 10-18</div>
                  </button>
                </div>
              </div>

              {/* Course Info */}
              {selectedCourse && (
                <div className="p-4 bg-[var(--ps-bg-elevated)] rounded-xl border border-[var(--ps-border)]">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-[var(--ps-text-primary)]">{selectedCourse.name}</p>
                      <p className="text-sm text-[var(--ps-text-muted)]">Par {selectedCourse.par || 72}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-green-500">{numberOfHoles}</span>
                      <span className="text-sm text-[var(--ps-text-muted)] ml-1">holes</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Players Summary */}
              <div className="p-4 bg-[var(--ps-bg-elevated)] rounded-xl border border-[var(--ps-border)]">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-blue-400" />
                  <div>
                    <p className="font-medium text-[var(--ps-text-primary)]">{selectedPlayers.length} Players</p>
                    <p className="text-xs text-[var(--ps-text-muted)]">
                      {selectedPlayers.map(p => p.name.split(' ')[0]).join(', ')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Scorecard Type Selection - Swipeable Carousel */}
          {currentStep === 3 && (
            <ScorecardCarousel
              scorecardTypes={SCORECARD_TYPES}
              selectedScorecardType={selectedScorecardType}
              onSelect={handleScorecardTypeSelect}
            />
          )}
        </div>

        {/* Footer - Always visible, fixed at bottom */}
        <div 
          className="p-4 border-t border-[var(--ps-border)] bg-[var(--ps-bg-surface)]"
          style={{ flexShrink: 0, paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        >
          {currentStep < 3 ? (
            <Button
              onClick={goToNextStep}
              disabled={currentStep === 1 && selectedPlayers.length < MIN_PLAYERS}
              className="w-full bg-green-600 hover:bg-green-500 text-white py-6"
              data-testid="next-step-btn"
            >
              {currentStep === 1 ? (
                <>
                  Continue to Round Setup
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              ) : (
                <>
                  Choose Scorecard
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={startRound}
              disabled={!selectedScorecardType || isCreating}
              className="w-full bg-green-600 hover:bg-green-500 text-white py-6"
              data-testid="start-round-btn"
            >
              {isCreating ? (
                'Creating Round...'
              ) : (
                <>
                  <Users className="w-5 h-5 mr-2" />
                  Start Round with {selectedPlayers.length} Players
                  <ChevronRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>

        {/* Scorecard Preview Modal */}
        {showScorecardPreview && previewCardType && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <div className="bg-[var(--ps-bg-surface)] rounded-2xl max-w-sm w-full overflow-hidden">
              {/* Preview Header */}
              <div className={`p-6 ${
                previewCardType.color === 'green' ? 'bg-green-500/20' :
                previewCardType.color === 'blue' ? 'bg-blue-500/20' :
                'bg-amber-500/20'
              }`}>
                <div className="flex items-center justify-center mb-4">
                  {(() => {
                    const Icon = previewCardType.icon;
                    return (
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${
                        previewCardType.color === 'green' ? 'bg-green-500 text-white' :
                        previewCardType.color === 'blue' ? 'bg-blue-500 text-white' :
                        'bg-amber-500 text-white'
                      }`}>
                        <Icon className="w-8 h-8" />
                      </div>
                    );
                  })()}
                </div>
                <h3 className="text-xl font-bold text-[var(--ps-text-primary)] text-center">
                  {previewCardType.name}
                </h3>
                <p className="text-sm text-[var(--ps-text-secondary)] text-center mt-1">
                  {previewCardType.description}
                </p>
              </div>
              
              {/* Preview Content */}
              <div className="p-6">
                {/* Simulated scorecard preview */}
                <div className="bg-[var(--ps-bg-elevated)] rounded-xl p-4 mb-4">
                  {previewCardType.id === 'quickcard' && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-green-500 flex items-center justify-center">
                          <Mic className="w-7 h-7 text-white" />
                        </div>
                      </div>
                      <p className="text-center text-sm text-[var(--ps-text-muted)]">Tap to speak scores</p>
                      <div className="grid grid-cols-5 gap-1 text-center text-xs">
                        {[1,2,3,4,5].map(h => (
                          <div key={h} className="py-1 bg-[var(--ps-bg-hover)] rounded">{h}</div>
                        ))}
                      </div>
                    </div>
                  )}
                  {previewCardType.id === 'voice' && (
                    <div className="space-y-3 text-center">
                      <div className="w-20 h-20 rounded-full bg-blue-500 mx-auto flex items-center justify-center">
                        <Mic className="w-10 h-10 text-white" />
                      </div>
                      <p className="text-sm text-[var(--ps-text-muted)]">&quot;Player 1 got a birdie&quot;</p>
                    </div>
                  )}
                  {previewCardType.id === 'manual' && (
                    <div className="space-y-2">
                      <div className="grid grid-cols-10 gap-1 text-xs text-center">
                        <div className="text-[var(--ps-text-muted)]">H</div>
                        {[1,2,3,4,5,6,7,8,9].map(h => (
                          <div key={h} className="text-[var(--ps-text-secondary)]">{h}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-10 gap-1 text-xs text-center">
                        <div className="text-[var(--ps-text-muted)]">P</div>
                        {[4,4,3,5,4,4,3,4,5].map((p, i) => (
                          <div key={i} className="text-green-400">{p}</div>
                        ))}
                      </div>
                      <div className="grid grid-cols-10 gap-1 text-xs text-center">
                        <div className="text-[var(--ps-text-muted)]">S</div>
                        {['-','-','-','-','-','-','-','-','-'].map((s, i) => (
                          <div key={i} className="py-1 bg-[var(--ps-bg-hover)] rounded text-[var(--ps-text-muted)]">{s}</div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                <h4 className="font-semibold text-[var(--ps-text-primary)] mb-2">Features:</h4>
                <ul className="space-y-1 mb-4">
                  {previewCardType.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-[var(--ps-text-secondary)]">
                      <Check className="w-4 h-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Preview Actions */}
              <div className="p-4 border-t border-[var(--ps-border)] flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowScorecardPreview(false)}
                  className="flex-1 border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
                >
                  Back
                </Button>
                <Button
                  onClick={confirmScorecardSelection}
                  disabled={isCreating}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white"
                  data-testid="confirm-scorecard-btn"
                >
                  {isCreating ? (
                    'Starting Round...'
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Start Round
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Add Guest Modal */}
        <Dialog open={showAddGuest} onOpenChange={setShowAddGuest}>
          <DialogContent className="max-w-sm bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
            <DialogHeader>
              <DialogTitle className="text-[var(--ps-text-primary)] flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-[var(--ps-text-muted)]" />
                Add Guest Player
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-[var(--ps-text-secondary)] text-sm">Name *</Label>
                <Input
                  placeholder="Enter guest name"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  className="bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)] mt-1"
                  data-testid="guest-name-input"
                />
              </div>
              <div>
                <Label className="text-[var(--ps-text-secondary)] text-sm">Handicap (optional)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 12.5"
                  value={guestHandicap}
                  onChange={(e) => setGuestHandicap(e.target.value)}
                  className="bg-[var(--ps-bg-elevated)] border-[var(--ps-border)] text-[var(--ps-text-primary)] mt-1"
                  data-testid="guest-handicap-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowAddGuest(false)}
                className="flex-1 border-[var(--ps-border)] text-[var(--ps-text-secondary)]"
              >
                Cancel
              </Button>
              <Button
                onClick={addGuest}
                disabled={!guestName.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-500 text-white"
                data-testid="confirm-add-guest-btn"
              >
                Add Guest
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Entry Fee Modal - Using unified EntryFeeSelector */}
        <Dialog open={showFeeSetup} onOpenChange={setShowFeeSetup}>
          <DialogContent className="max-w-md bg-[var(--ps-bg-surface)] border-[var(--ps-border)]">
            <DialogHeader>
              <DialogTitle className="text-[var(--ps-text-primary)] flex items-center gap-2">
                Set Entry Fee
              </DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <EntryFeeSelector
                entryFee={entryFee}
                entryFeeType={entryFeeType}
                onChangeValue={setEntryFee}
                onChangeType={setEntryFeeType}
                playerCount={selectedPlayers.length}
                showTotalPot={true}
              />
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={() => setShowFeeSetup(false)}
                  className="bg-green-600 hover:bg-green-500 text-white"
                >
                  Done
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default GroupRoundSetup;
export { GroupRoundSetup };
